import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, AdminUser, AdminPlace, AdminLoginResponse, clearAxiosCookies } from '../services/api';
import { clearAllCookies, clearAuthCookies } from '../utils/cookieUtils';
import legalService from '../services/legalService';

interface AuthContextType {
  user: AdminUser | null;
  currentPlace: AdminPlace | null;
  places: AdminPlace[];
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresTermsAcceptance: boolean;
  requestLoginCode: (email: string) => Promise<void>;
  verifyLoginCode: (contact: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  setCurrentPlace: (place: AdminPlace) => void;
  validateAuth: () => Promise<boolean>;
  checkTermsAcceptance: () => Promise<void>;
  markTermsAccepted: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [places, setPlaces] = useState<AdminPlace[]>([]);
  const [currentPlace, setCurrentPlace] = useState<AdminPlace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(false);

  // Função para limpar dados de autenticação
  const clearAuthData = () => {
    // Limpar localStorage
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_places');
    localStorage.removeItem('admin_current_place');
    
    // Limpar cookies de autenticação
    clearAuthCookies();
    
    // Limpar estado local
    setUser(null);
    setPlaces([]);
    setCurrentPlace(null);
  };

  // Função para validar se a autenticação ainda é válida
  const validateAuth = async (): Promise<boolean> => {
    if (!user) return false;
    
    try {
      // Fazer uma requisição simples para verificar se a sessão ainda é válida
      // Se retornar 401, a sessão expirou
      await authService.validateSession();
      return true;
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.log('Session expired, clearing auth data');
        clearAuthData();
        return false;
      }
      // Para outros erros, assumir que ainda está autenticado
      return true;
    }
  };

  useEffect(() => {
    // Verificar se há dados de usuário salvos
    const savedUser = localStorage.getItem('admin_user');
    const savedPlaces = localStorage.getItem('admin_places');
    const savedCurrentPlace = localStorage.getItem('admin_current_place');

    if (savedUser && savedPlaces) {
      try {
        const userData = JSON.parse(savedUser);
        const placesData = JSON.parse(savedPlaces);
        const currentPlaceData = savedCurrentPlace ? JSON.parse(savedCurrentPlace) : null;

        setUser(userData);
        setPlaces(placesData);
        setCurrentPlace(currentPlaceData || (placesData.length > 0 ? placesData[0] : null));
        
        // Verificar aceite dos termos se não for super admin
        if (userData && !userData.isSystemSuperAdmin) {
          checkTermsAcceptance();
        }
      } catch (error) {
        console.error('Error parsing saved auth data:', error);
        clearAuthData();
      }
    }
    setIsLoading(false);

    // Listener para evento de token expirado
    const handleTokenExpired = () => {
      console.log('AuthContext: Token expirado detectado, limpando dados de autenticação');
      clearAuthData();
      // Redirecionar para página de token expirado
      if (window.location.pathname !== '/token-expired') {
        console.log('AuthContext: Redirecionando para /token-expired');
        // window.location.href = '/token-expired';
      }
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  const requestLoginCode = async (email: string) => {
    try {
      setIsLoading(true);
      await authService.requestLoginCode(email);
    } catch (error) {
      console.error('Error requesting login code:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyLoginCode = async (contact: string, code: string) => {
    try {
      setIsLoading(true);
      const response: AdminLoginResponse = await authService.verifyLoginCode(contact, code);

      // Salvar dados no localStorage
      localStorage.setItem('admin_user', JSON.stringify(response.user));
      localStorage.setItem('admin_places', JSON.stringify(response.places));
      
      // Definir primeira cafeteria como atual (se houver)
      const firstPlace = response.places.length > 0 ? response.places[0] : null;
      if (firstPlace) {
        localStorage.setItem('admin_current_place', JSON.stringify(firstPlace));
        setCurrentPlace(firstPlace);
      }

      setUser(response.user);
      setPlaces(response.places);
      
      // Verificar aceite dos termos após login bem-sucedido
      console.log('AuthContext: Usuário logado, verificando aceite dos termos...');
      console.log('AuthContext: Usuário:', response.user);
      console.log('AuthContext: isSystemSuperAdmin:', response.user.isSystemSuperAdmin);
      
      if (!response.user.isSystemSuperAdmin) {
        console.log('AuthContext: Usuário não é super admin, verificando aceite dos termos...');
        await checkTermsAcceptance();
      } else {
        console.log('AuthContext: Usuário é super admin, pulando verificação de aceite dos termos');
      }
    } catch (error) {
      console.error('Error verifying login code:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    console.log('AuthContext: Iniciando logout...');
    
    // Log dos cookies antes da limpeza
    console.log('Cookies antes da limpeza:', document.cookie);
    
    try {
      // Primeiro, fazer logout no servidor para limpar cookies HTTP-only
      await authService.logout();
      console.log('AuthContext: Logout da API realizado com sucesso');
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
      // Mesmo com erro, continuar com a limpeza local
    } finally {
      console.log('AuthContext: Limpando dados locais...');
      clearAuthData();
      
      // Limpeza adicional para garantir que tudo seja removido
      clearAllCookies();
      clearAxiosCookies(); // Adicionado para limpar cookies do axios
      
      // Log dos cookies após a limpeza
      console.log('Cookies após a limpeza:', document.cookie);
      
      console.log('AuthContext: Logout concluído');
    }
  };

  const handleSetCurrentPlace = (place: AdminPlace) => {
    setCurrentPlace(place);
    localStorage.setItem('admin_current_place', JSON.stringify(place));
  };

  const checkTermsAcceptance = async () => {
    try {
      console.log('AuthContext: Iniciando verificação de aceite dos termos...');
      
      // Verificar cache local primeiro para evitar requests desnecessários
      const cacheKey = 'legal_acceptance_cache';
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          const now = Date.now();
          const cacheAge = now - parsed.timestamp;
          const cacheExpiry = 5 * 60 * 1000; // 5 minutos
          
          if (cacheAge < cacheExpiry && !parsed.requiresAcceptance) {
            console.log('AuthContext: Usando cache local - usuário já aceitou os termos');
            setRequiresTermsAcceptance(false);
            return;
          }
        } catch (error) {
          console.error('AuthContext: Erro ao parsear cache:', error);
        }
      }
      
      const response = await legalService.checkTermsAcceptanceRequired();
      console.log('AuthContext: Resposta do serviço:', response);
      setRequiresTermsAcceptance(response.requiresAcceptance);
      console.log('AuthContext: requiresTermsAcceptance definido como:', response.requiresAcceptance);
    } catch (error) {
      console.error('Error checking terms acceptance:', error);
      setRequiresTermsAcceptance(false); // Assume false on error
    }
  };

  const markTermsAccepted = () => {
    setRequiresTermsAcceptance(false);
    // No localStorage para salvar, pois é uma ação de usuário
  };

  const value: AuthContextType = {
    user,
    currentPlace,
    places,
    isAuthenticated: !!user,
    isLoading,
    requiresTermsAcceptance,
    requestLoginCode,
    verifyLoginCode,
    logout,
    setCurrentPlace: handleSetCurrentPlace,
    validateAuth,
    checkTermsAcceptance,
    markTermsAccepted,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 